import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import {
  useForm,
  Control,
  Controller,
  useWatch,
  useFormContext,
  FormProvider,
} from 'react-hook-form';

import { date as commonDate } from '@finley-technologies/common';

import { usePortfolioFacilitiesStore } from '../PortfolioFacilitiesProvider';
import {
  FacilitiesFilterState,
  NumericRangeFilter,
  StaticDateRangeFormState,
  StaticNumericRangeFormState,
} from '../PortfolioFacilitiesProvider/facilitiesFilterTypes';
import { IsolatedMultiSelectInput } from 'packages/common-frontend/components/InputsBETA/IsolatedMultiSelectInput';
import {
  TextInput,
  MaskTypes,
} from 'packages/common-frontend/components/InputsBETA/TextInput';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { getAppliedFiltersCount } from '~/core/routes/credit-facilities/facilityFiltersDrawer/dataMappings';
import { colors } from '~/packages/common-frontend/colors';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';

// TODO potentially make a generic component in InputsBETA for this
const MaxRangeInput = ({
  minFieldName,
  maxFieldName,
  placeholder,
  mask = 'usd',
}: {
  minFieldName: any;
  maxFieldName: any;
  placeholder: string;
  mask?: MaskTypes;
}) => {
  const { trigger } = useFormContext();
  const rangeMin = useWatch<NumericRangeFilter>({
    name: minFieldName,
  });

  useEffect(() => {
    trigger([maxFieldName]);
  }, [maxFieldName, rangeMin, trigger]);

  return (
    <TextInput<NumericRangeFilter>
      name={maxFieldName}
      placeholder={placeholder}
      mask={mask}
      customRules={{
        min: {
          value: Number(rangeMin),
          message: 'Maximum must be greater than or equal to minimum',
        },
      }}
    />
  );
};

const FiltersTabsIterable = ['Standard', 'Capital Provider'] as const;
type FiltersTabsTypes = typeof FiltersTabsIterable[number];

const DateRangeFormInput = ({
  name,
  label,
  formControl,
}: {
  name: keyof StaticDateRangeFormState;
  label: string;
  formControl: Control<FacilitiesFilterState>;
}) => (
  <FormControl>
    <FormLabel>
      <Typography variant="section-title">{label}</Typography>
    </FormLabel>
    <Controller
      name={`staticFilters.${name}`}
      control={formControl}
      defaultValue={{ startDate: null, endDate: null }}
      render={({ field }) => (
        <FullDateRangePicker
          onSelectDateRange={([startDate, endDate]) => {
            field.onChange({
              startDate: startDate
                ? commonDate.dateToFullDateString(startDate)
                : null,
              endDate: endDate
                ? commonDate.dateToFullDateString(endDate)
                : null,
            });
          }}
          currentDateRange={{
            startDate: field.value?.startDate
              ? commonDate.fullDateStrToDate(field.value.startDate)
              : null,
            endDate: field.value?.endDate
              ? commonDate.fullDateStrToDate(field.value.endDate)
              : null,
          }}
          width="100%"
        />
      )}
    />
  </FormControl>
);

const NumericRangeFormInput = ({
  name,
  label,
  mask,
}: {
  name: keyof StaticNumericRangeFormState;
  label: string;
  mask: MaskTypes;
}) => (
  <FormControl>
    <FormLabel>
      <Typography variant="section-title">{label}</Typography>
      <FormHelperText>Enter a minimum and/or maximum value.</FormHelperText>
    </FormLabel>
    <Flex gap="1rem">
      <TextInput<FacilitiesFilterState>
        name={`staticFilters.${name}.min`}
        placeholder="Min amount"
        mask={mask}
      />
      <MaxRangeInput
        minFieldName={`staticFilters.${name}.min`}
        maxFieldName={`staticFilters.${name}.max`}
        placeholder="Max amount"
        mask={mask}
      />
    </Flex>
  </FormControl>
);

export const FacilitiesFiltersDrawer = () => {
  const drawerControls = useDisclosure();
  const filtersBtnRef = useRef(null);
  const { user } = useUserAuthOrThrow();

  const filters = usePortfolioFacilitiesStore((store) => store.filters);
  const initialFilters = usePortfolioFacilitiesStore(
    (store) => store.initialFilters,
  );
  const setFilters = usePortfolioFacilitiesStore((store) => store.setFilters);
  const appliedFiltersCount = usePortfolioFacilitiesStore(
    getAppliedFiltersCount,
  );

  const formMethods = useForm<FacilitiesFilterState>({
    defaultValues: initialFilters,
    mode: 'onBlur',
  });
  useEffect(() => {
    formMethods.reset(initialFilters);
  }, [initialFilters, formMethods]);

  const handleResetFilters = () => formMethods.reset(initialFilters);

  const onSubmit = (formData: FacilitiesFilterState) => {
    setFilters(formData); // update filters INSTEAD of just set- @todo @mark
    drawerControls.onClose();
  };

  const [selectedTab, setSelectedTab] = useState<FiltersTabsTypes>('Standard');

  return (
    <>
      <Button
        variant="secondary"
        colorScheme="v2-aurora"
        ref={filtersBtnRef}
        onClick={drawerControls.onOpen}
        leftIcon={<Icon icon="filter" />}
        rightIcon={
          appliedFiltersCount > 0 ? (
            <Box
              background={colors.gray[100]}
              borderRadius="0.25rem"
              padding="0.125rem 0.5rem"
            >
              {appliedFiltersCount}
            </Box>
          ) : undefined
        }
        p="0.5rem 1.5rem"
      >
        Filters
      </Button>
      <Drawer
        placement="right"
        finalFocusRef={filtersBtnRef}
        size="xl"
        isOpen={drawerControls.isOpen}
        onClose={drawerControls.onClose}
      >
        <FormProvider {...formMethods}>
          <DrawerOverlay />
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
                <Typography variant="page-title">Filters</Typography>
              </DrawerHeader>
              <DrawerBody px="3rem">
                <HStack gap="1rem" mb="2rem">
                  {FiltersTabsIterable.map((tab) => (
                    <Button
                      key={tab}
                      variant="primary-muted"
                      colorScheme="gray"
                      isActive={selectedTab === tab}
                      onClick={() => setSelectedTab(tab)}
                      size="sm"
                    >
                      {tab === 'Capital Provider'
                        ? user.organization.name
                        : tab}
                    </Button>
                  ))}
                </HStack>

                {selectedTab === 'Standard' && (
                  <VStack spacing="1.5rem" align="flex-start">
                    <IsolatedMultiSelectInput<FacilitiesFilterState>
                      formLabelStyle={{ variant: 'section-title' }}
                      label="Facility"
                      titleText="All facilities"
                      placeholder="Select facilities"
                      name="staticFilters.facilityIds.selectedOptions"
                      options={Array.from(
                        filters.staticFilters.facilityIds.options,
                      ).map((option) => ({
                        label: option.name,
                        value: option.id,
                      }))}
                    />
                    <DateRangeFormInput
                      name="maturityDate"
                      label="Maturity Date"
                      formControl={formMethods.control}
                    />
                    <DateRangeFormInput
                      name="closingDate"
                      label="Closing Date"
                      formControl={formMethods.control}
                    />
                    <DateRangeFormInput
                      name="dateOfLastFundingRequest"
                      label="Last Funding Date"
                      formControl={formMethods.control}
                    />

                    <NumericRangeFormInput
                      name="globalCommitmentAmount"
                      label="Global Commitment Amount"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="globalLoanOutstanding"
                      label="Global Outstanding Balance"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="globalUnusedCommitment"
                      label="Global Unused Commitment"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="globalUtilization"
                      label="Global Utilization (%)"
                      mask="percentage"
                    />
                    <NumericRangeFormInput
                      name="collectionsAccountBalance"
                      label="Collections Account Balance"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="requiredReserve"
                      label="Required Reserve"
                      mask="percentage"
                    />
                    <NumericRangeFormInput
                      name="totalBorrowingBase"
                      label="Total Borrowing Base"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="borrowingBaseFromReceivables"
                      label="Borrowing Base from Receivables"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="borrowingBaseAvailability"
                      label="Borrowing Base Availability"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="accruedInterestAndFees"
                      label="Accrued Interest and Fees"
                      mask="usd"
                    />
                  </VStack>
                )}

                {selectedTab === 'Capital Provider' && (
                  <VStack spacing="1.5rem" align="flex-start">
                    <NumericRangeFormInput
                      name="capitalProviderCommitmentAmount"
                      label="Commitment Amount"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="capitalProviderLoanOutstanding"
                      label="Outstanding Balance"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="capitalProviderUnused"
                      label="Unused Commitment"
                      mask="usd"
                    />
                    <NumericRangeFormInput
                      name="capitalProviderUtilization"
                      label="Utilization (%)"
                      mask="percentage"
                    />
                  </VStack>
                )}
              </DrawerBody>
              <DrawerFooter justifyContent="space-between" m="1.5rem">
                <Button
                  variant="secondary"
                  colorScheme="v2-aurora"
                  onClick={handleResetFilters}
                >
                  Reset Filters
                </Button>
                <Button variant="primary" colorScheme="v2-aurora" type="submit">
                  Apply Filters
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </form>
        </FormProvider>
      </Drawer>
    </>
  );
};
